/* eslint-disable no-mixed-spaces-and-tabs */
import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MultilevelNode, NgMaterialMultilevelMenuComponent, NgMaterialMultilevelMenuModule } from 'ng-material-multilevel-menu';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MENU_MODULE_NAMES, SITE_SETTINGS } from '@app/constants';
import { AuthService, AuthenticationInfo, NavigationService, TitleService } from '@app/core/services';
import { MenuItem } from '@app/models';
import { MenuService } from './services/menu-service';

@Component({
	selector: 'app-menu',
	templateUrl: './menu.component.html',
	standalone: true,
	imports: [NgIf, NgMaterialMultilevelMenuModule],
})
export class MenuComponent implements OnInit, OnDestroy {
	@ViewChild(NgMaterialMultilevelMenuComponent) MultilevelMenuComponent: NgMaterialMultilevelMenuComponent;

	menuConfig = {
		interfaceWithRoute: false,
		highlightOnSelect: false,
		useDividers: false,
		collapseOnSelect: true,
		listBackgroundColor: 'inherit',
		fontColor: 'inherit',
	};
	allMenuItems: MenuItem[];
	menuItems: MultilevelNode[];
	moduleName: string;

	private readonly destroy$ = new Subject();

	constructor(
		private readonly navigationService: NavigationService,
		private readonly menuService: MenuService,
		private readonly authService: AuthService,
		private readonly titleService: TitleService,
		private readonly router: Router
	) {}

	ngOnInit(): void {
		this.navigationService.route$.pipe(takeUntil(this.destroy$)).subscribe((route) => {
			this.navigationService.sideNavOpened$.next(false);
			const newModuleName = this.getNewModuleName(route);

			if (!this.moduleName || newModuleName !== this.moduleName) {
				this.moduleName = newModuleName;
				if (this.allMenuItems?.length) {
					this.setDisplayedMenuItems();
				}
			}
			this.MultilevelMenuComponent?.updateNodeByURL(this.navigationService.route);
			this.setPageTitle();
		});

		this.authService.authentication$.pipe(takeUntil(this.destroy$)).subscribe((authentication: AuthenticationInfo) => {
			if (authentication.accessToken) {
				this.loadMenuItems();
			} else {
				this.menuService.setMenuItems(null);
				this.allMenuItems = [];
				this.setPageTitle();
			}
		});
	}

	getNewModuleName(route: string): string {
		if (!route) {
			return '';
		}
		const map = {
			fund: MENU_MODULE_NAMES.fundManagement,
			payroll: MENU_MODULE_NAMES.payroll,
			globalsettings: MENU_MODULE_NAMES.systemSettings,
			systemsettings: MENU_MODULE_NAMES.systemSettings,
			'system-settings': MENU_MODULE_NAMES.systemSettings,
			lsb: MENU_MODULE_NAMES.linqSchoolBookkeeping,
			default: '',
		};

		const routeArray = route.toLowerCase().split('/');
		let index = -1;
		let claimSection = map.default;
		for (const [key] of Object.entries(map)) {
			if (routeArray.includes(key) && routeArray.indexOf(key) > index) {
				index = routeArray.indexOf(key);
				claimSection = map[key];
			}
		}
		return claimSection;
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	private loadMenuItems(): void {
		this.menuService.getMenuItems().subscribe((data: MenuItem[]) => {
			this.menuService.setMenuItems(data);
			this.allMenuItems = data;
			this.allMenuItems.forEach((item: MenuItem) => this.makeLinkRelative(item));
			this.setDisplayedMenuItems();
			this.setPageTitle();
		});
	}
	private readonly onMenuItemClick = (link) => {
		requestAnimationFrame(() => {
			this.router.navigateByUrl(link);
			this.setDisplayedMenuItems();
			this.navigationService.sideNavOpened$.next(false);
		});
	};

	private makeLinkRelative(obj: MenuItem) {
		if (obj.items) {
			obj.items.forEach((item: MenuItem) => this.makeLinkRelative(item));
		} else if (obj.link) {
			obj.link = obj.link.startsWith('/') ? obj.link : `/${obj.link}`;
			obj.onSelected = () => {
				this.onMenuItemClick(obj.link);
			};
		}
	}

	private setDisplayedMenuItems(): void {
		if (!this.moduleName) {
			this.moduleName = this.getNewModuleName(this.router.url) || '';
		}

		const filteredMenuItems = this.allMenuItems.find((item) => item.Module === this.moduleName)?.items || [];

		const dashboardLabel = 'Dashboard';
		if (!filteredMenuItems.some((x) => x.label === dashboardLabel)) {
			filteredMenuItems.unshift({
				label: dashboardLabel,
				link: '/home',
				onSelected: () => this.onMenuItemClick('/home'),
			} as MultilevelNode);
		}

		this.menuItems = filteredMenuItems;

		setTimeout(() => {
			this.MultilevelMenuComponent?.updateNodeByURL(this.navigationService.route);
			this.setPageTitle();
		}, 100);
	}

	private setPageTitle(): void {
		const menuItemsToSearch = (this.allMenuItems || []) as MultilevelNode[];
		const matchedMenuItem = this.getMenuItemByLink(menuItemsToSearch, this.navigationService.route);
		let pageTitle = SITE_SETTINGS.name;
		if (matchedMenuItem) {
			pageTitle = `${matchedMenuItem.label} - ${pageTitle}`;
		}
		this.titleService.setTitle(pageTitle);
	}

	private getMenuItemByLink(menuItems: MultilevelNode[], link: string): MultilevelNode {
		if (menuItems) {
			for (const menuItem of menuItems) {
				if (menuItem.link === link) {
					return menuItem;
				}

				const foundSubItem = this.getMenuItemByLink(menuItem.items, link);
				if (foundSubItem) {
					return foundSubItem;
				}
			}
		}
		return null;
	}
}
